import React, { useMemo, useCallback } from 'react';
import { PropTypes } from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useIsColumnarTotalsEnabled } from '~/modules/resourcing/hooks/useIsColumnarTotalsEnabled';
import {
  MergeResourceRequestDialog,
  SecondaryActions,
  ResourceRequestDeleteActions,
  ResourceAllocationDeleteActions,
  ReleaseResourceRequestDialog,
  RequestActionButton
} from '~/modules/resourcing/common/components';
import {
  useRequestStatusHandlers,
  useSecondaryResourceRequestActionHandlers
} from '~/modules/resourcing/common/hooks';
import useDeleteResourceRequestHandler from '~/modules/resourcing/common/hooks/useDeleteResourceRequestHandler';
import {
  useRedirectToAssignResource,
  useResourceAllocationActionHandlers
} from '~/modules/resourcing/hooks';
import { useAppContext } from '~/modules/App/AppContext';
import {
  useSessionStorage,
  useGetKeyValueSettings,
  useDialogState,
  useIsPtiEnabled
} from '~/modules/common/hooks';
import { HiringStatus, ResourceRequestStatus } from '~/types';
import useMarkResourceRequestAsCompleteAndMerge from '~/modules/resourcing/common/hooks/useMarkResourceRequestAsCompleteAndMerge';
import useMarkToBeHiredResourceRequestWithHiringRequestedSubStatus from '~/modules/resourcing/common/hooks/useMarkToBeHiredResourceRequestWithHiringRequestedSubStatus';
import { WithdrawHiringRequestDialog } from '~/modules/common/components';
import { useWithdrawHiringRequestDialogState } from '~/modules/common/components/WithdrawHiringRequestDialog';
import { useGetResourceRequestFilter } from '~/modules/resourcing/common/hooks/useGetResourceRequestFilter';
import { RESOURCE_ALLOCATION_CHART_TAB_ENUM } from '~/modules/resourcing/hooks/useResourceAllocationChartTabStateContext';
import { useUpdateCompletedResourceAllocationProjectCacheOnAddWithProposedRequest2 } from '../ResourceAllocationChart/hooks/useUpdateCompletedResourceAllocationCache';
import useStyles from './useStyles';
import { useResourceRequestDrawerActions } from './hooks';
import { getCacheUpdateFunctions } from './cacheUpdateFunctions';

export const ResourceRequestDrawerActions = ({
  allocationChartRef,
  cacheUpdateParams,
  canEditProject,
  canEditResourceRequest,
  canEditTask,
  canEditTeam,
  chartSettingsKey,
  drawerOnClose,
  hasError = false,
  isProjectResourcingView,
  isResourceRequestMergeEnabled,
  mergeDialogState,
  onSaveClick,
  resourceAllocations,
  resourceRequest,
  setResourceRequestSaving
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const onResourceAcceptCacheUpdate = useUpdateCompletedResourceAllocationProjectCacheOnAddWithProposedRequest2(
    {
      allocationChartRef
    }
  );
  const isPtiEnabled = useIsPtiEnabled();
  const isShowColumnarTotalsEnabled = useIsColumnarTotalsEnabled();

  const { storedValue } = useSessionStorage(
    'HIRING-STATUS-FILTER-QUERY-AND-CACHE-VARIABLES',
    {}
  );

  const {
    hiringStatusSummaryCountQueryVariables,
    cacheVariables
  } = storedValue;

  const isHiringStatusSubFilterCacheUpdateEnabled =
    isPtiEnabled &&
    cacheVariables?.resourcingPlanTab ===
      RESOURCE_ALLOCATION_CHART_TAB_ENUM.TOBEHIRED;

  const { dispatch } = useAppContext();

  const { storedValue: resourceRequestsQueryVariables } = useSessionStorage(
    'RESOURCE-REQUESTS-QUERY-VARIABLES',
    null
  );

  const filter = useGetResourceRequestFilter();

  const isHiringRequested =
    isPtiEnabled &&
    resourceRequest?.sourceMetadata?.hiringStatus ===
      HiringStatus.HiringRequested;

  const { keyValueSettings: chartSettings } = useGetKeyValueSettings(
    chartSettingsKey || 'resource_plan_chart_settings'
  );
  const isResourceActualModeEnabled = chartSettings?.showActuals || false;

  const resourcePlanFilter =
    Object.keys(resourceRequestsQueryVariables?.filter || []).length !== 0
      ? resourceRequestsQueryVariables.filter
      : null;

  const cacheUpdateFunctions = getCacheUpdateFunctions({
    request: resourceRequest,
    filter,
    hiringStatusSummaryCountQueryVariables,
    resourcePlanTabVariables: cacheVariables,
    resourceRequestsQueryVariables,
    isHiringStatusSubFilterCacheUpdateEnabled,
    isProjectResourcingView,
    isPtiEnabled,
    isResourceActualModeEnabled,
    skipProjectTotalsCacheUpdate: isShowColumnarTotalsEnabled,
    onResourceAcceptCacheUpdate,
    resourcePlanFilter
  });

  const {
    onResourceRequestDeleteCacheFunctions,
    onResourceRequestUpdateHiringStatusCacheFunctions
  } = cacheUpdateFunctions();

  const { deleteResourceRequestFromProject } = useDeleteResourceRequestHandler({
    isResourceActualModeEnabled,
    cacheUpdateFunctions: onResourceRequestDeleteCacheFunctions || []
  });

  const {
    existingAllocationState: {
      hasExistingAllocation,
      hasExistingAllocationLoading
    }
  } = mergeDialogState;

  const { onRemoveAllResources } = useResourceAllocationActionHandlers({
    resourceRequest
  });

  const req = useMemo(
    () => ({
      ...resourceRequest,
      resourceAllocations
    }),
    [resourceRequest, resourceAllocations]
  );

  const {
    onMarkResourceRequestAsCompleteAndMerge,
    isLoading: isMergeLoading
  } = useMarkResourceRequestAsCompleteAndMerge({
    resourceRequest,
    allocationChartRef,
    cacheUpdateParams: {
      ...cacheUpdateParams,
      ...{ planFilter: resourcePlanFilter }
    }
  });

  const history = useHistory();

  const onAssignResourceAction = useRedirectToAssignResource({
    history,
    resourceRequest,
    isResourcingTab: isProjectResourcingView,
    scale: chartSettings?.scale,
    displayUnit: chartSettings?.displayUnit
  });

  const {
    onResourceRequestDuplicate,
    onResourceRequestSplit
  } = useSecondaryResourceRequestActionHandlers({
    resourceRequest,
    isResourceActualModeEnabled
  });

  const {
    open: showReleaseResourcesDialog,
    openDialog: openReleaseResourcesDialog,
    closeDialog: _closeReleaseResourcesDialog
  } = useDialogState(false);

  const closeReleaseResourcesDialog = useCallback(
    (event, reason) => {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;

      _closeReleaseResourcesDialog();
    },
    [_closeReleaseResourcesDialog]
  );

  const {
    open: showRemoveAllocationDialog,
    closeDialog: closeRemoveAllocationDialog
  } = useDialogState(false);

  const {
    open: showRemoveRequestDialog,
    openDialog: openRemoveRequestDialog,
    closeDialog: closeRemoveRequestDialog
  } = useDialogState(false);

  const {
    open: showWithdrawHiringRequestDialog,
    openDialog: openWithdrawHiringRequestDialog,
    closeDialog: closeWithdrawDialog,
    withdrawHiringRequestDialogState,
    setWithdrawHiringRequestDialog
  } = useWithdrawHiringRequestDialogState();

  const onDeleteRequestClick = useCallback(() => {
    closeRemoveRequestDialog();
    deleteResourceRequestFromProject({
      resourceRequestsQueryVariables: resourceRequestsQueryVariables
        ? {
            ...resourceRequestsQueryVariables,
            projectUri: resourceRequest.projectUri
          }
        : null,
      request: resourceRequest
    });
    dispatch({
      type: 'RESOURCE-REQUEST-DRAWER-BUTTON-ACTION',
      resourceRequestButtonActionMessage: intl.formatMessage({
        id: 'resourceRequestActions.requestRemoved'
      })
    });
    drawerOnClose();
  }, [
    closeRemoveRequestDialog,
    deleteResourceRequestFromProject,
    resourceRequest,
    resourceRequestsQueryVariables,
    dispatch,
    intl,
    drawerOnClose
  ]);

  const onDeleteAllocationClick = useCallback(() => {
    closeRemoveAllocationDialog();
    onRemoveAllResources();
  }, [closeRemoveAllocationDialog, onRemoveAllResources]);

  const {
    onMarkToBeHiredResourceRequestWithHiringRequestedSubStatus: onResourceRequestHiringStatusUpdate
  } = useMarkToBeHiredResourceRequestWithHiringRequestedSubStatus({
    cacheUpdateFunctions:
      onResourceRequestUpdateHiringStatusCacheFunctions || []
  });

  const {
    onResourceRequestToBeHired,
    onResourceRequestSubmit,
    onResourceRequestAccept,
    onResourceRequestReject,
    onResourceRequestAllocationReject,
    onResourceRequestFulfilled,
    onResourceRequestUnsubmit,
    onReleaseResourceAllocationsForRequest,
    onResourceRequestMerge
  } = useRequestStatusHandlers({
    resourceRequest,
    allocationChartRef,
    mergeDialogState,
    onMarkResourceRequestAsCompleteAndMerge,
    getCacheUpdateFunctions: cacheUpdateFunctions
  });

  const actions = useResourceRequestDrawerActions({
    canEditProject,
    canEditResourceRequest,
    canEditTeam,
    closeDrawer: drawerOnClose,
    hasExistingAllocationLoading,
    history,
    isHiringRequested,
    isProjectResourcingView,
    isResourceRequestMergeEnabled,
    onAssignResourceAction,
    onDeleteRequestClick,
    onResourceRequestAccept,
    onResourceRequestAllocationReject,
    onResourceRequestDuplicate,
    onResourceRequestFulfilled,
    onResourceRequestHiringStatusUpdate,
    onResourceRequestMerge,
    onResourceRequestReject,
    onResourceRequestSplit,
    onResourceRequestSubmit,
    onResourceRequestToBeHired,
    onResourceRequestUnsubmit,
    onSaveResourceRequest: onSaveClick,
    openReleaseResourcesDialog,
    openRemoveRequestDialog,
    openWithdrawHiringRequestDialog,
    resourceRequest: req,
    setResourceRequestSaving,
    setSnackBarMessage: dispatch,
    setWithdrawHiringRequestDialog
  });

  const secondaryActions = actions
    .filter(action => action.secondary)
    .map(action => ({
      ...action,
      disabled: action.id === 'remove-request' ? false : hasError
    }));
  const primaryActions = actions.filter(action => !action.secondary);

  if (primaryActions.length === 0 && secondaryActions.length === 0) return null;

  return (
    <div className={classes.root}>
      {primaryActions.length > 0 &&
        primaryActions.map((primaryAction, index) => {
          const Component = primaryAction.buttonComponent
            ? primaryAction.buttonComponent
            : RequestActionButton;

          return (
            <Component
              key={primaryAction.label}
              disabled={hasError}
              className={index > 0 ? classes.buttonSpacing : null}
              {...primaryAction}
            />
          );
        })}

      {secondaryActions.length > 0 && (
        <>
          <div className={classes.spacer} />
          <SecondaryActions actions={secondaryActions} />
          {showRemoveRequestDialog && (
            <ResourceRequestDeleteActions
              showRemoveRequestDialog={showRemoveRequestDialog}
              closeRemoveRequestDialog={closeRemoveRequestDialog}
              onDeleteRequestClick={onDeleteRequestClick}
            />
          )}
          {showRemoveAllocationDialog && (
            <ResourceAllocationDeleteActions
              showRemoveAllocationDialog={showRemoveAllocationDialog}
              closeRemoveAllocationDialog={closeRemoveAllocationDialog}
              onDeleteAllocationClick={onDeleteAllocationClick}
            />
          )}
          {showReleaseResourcesDialog && (
            <ReleaseResourceRequestDialog
              resourceRequest={req}
              onResourceRequestSubmit={onResourceRequestSubmit}
              onReleaseResourceAllocationsForRequest={
                onReleaseResourceAllocationsForRequest
              }
              open={showReleaseResourcesDialog}
              onCancelClick={closeReleaseResourcesDialog}
              canEditTask={canEditTask}
            />
          )}
        </>
      )}
      {isResourceRequestMergeEnabled &&
        (resourceRequest.requestStatus === ResourceRequestStatus.Tentative ||
          resourceRequest.requestStatus === ResourceRequestStatus.Submitted ||
          resourceRequest.requestStatus ===
            ResourceRequestStatus.Allocationrejected) &&
        hasExistingAllocation && (
          <MergeResourceRequestDialog
            open={mergeDialogState.open}
            onClose={mergeDialogState.closeDialog}
            resourceRequest={resourceRequest}
            onMarkResourceRequestAsCompleteAndMerge={
              onMarkResourceRequestAsCompleteAndMerge
            }
            isMergeLoading={isMergeLoading}
          />
        )}
      {isHiringRequested && showWithdrawHiringRequestDialog && (
        <WithdrawHiringRequestDialog
          open={showWithdrawHiringRequestDialog}
          onClose={closeWithdrawDialog}
          onClick={withdrawHiringRequestDialogState.onClick}
          actionType={withdrawHiringRequestDialogState.actionType}
        />
      )}
    </div>
  );
};

ResourceRequestDrawerActions.propTypes = {
  allocationChartRef: PropTypes.object,
  cacheUpdateParams: PropTypes.object,
  canEditProject: PropTypes.bool,
  canEditResourceRequest: PropTypes.bool,
  canEditTask: PropTypes.bool,
  canEditTeam: PropTypes.bool.isRequired,
  chartSettingsKey: PropTypes.string,
  drawerOnClose: PropTypes.func,
  hasError: PropTypes.bool,
  isProjectResourcingView: PropTypes.bool,
  isResourceRequestMergeEnabled: PropTypes.bool,
  mergeDialogState: PropTypes.object,
  onSaveClick: PropTypes.func,
  resourceAllocations: PropTypes.array,
  resourceRequest: PropTypes.object,
  setResourceRequestSaving: PropTypes.func
};

export default ResourceRequestDrawerActions;
